import React, { useEffect, useMemo, useState } from "react";
import { Switch, Select, Input, Button, Table, Tag, Spin } from "antd";
import { FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import DetailSection from "../commoncomponent/DetailSection";
import PermissionGate from "../commoncomponent/PermissionGate";
import AdminOtpModal from "../commoncomponent/AdminOtpModal";
import {
  sendCryptoWithdrawOtpApi,
  getWithdrawEnabledCoins,
  getWithdrawCoinNetworks,
  toggleUserCryptoWithdraw,
  addUserWithdrawAddress,
  toggleUserWithdrawAddress,
  deleteUserWithdrawAddress,
} from "../../../service/api";
import { validateWithdrawAddress } from "../../../utils/addressValidation";

// Per-user Crypto Withdraw Manager — admin-managed address whitelist.
// When enabled, the admin curates the exact set of destination addresses this
// user is allowed to withdraw crypto to (multiple per coin+network allowed).
// Flatten the grouped coin → network → addresses tree into table rows, each
// carrying the coin symbol and chain it belongs to.
const flattenConfig = (cfg) => {
  const rows = [];
  for (const c of cfg?.coins ?? []) {
    for (const n of c.networks ?? []) {
      for (const a of n.addresses ?? []) {
        rows.push({
          _id: a._id,
          address: a.address,
          memo: a.memo,
          isActive: a.isActive,
          symbol: c.symbol,
          coinId: c.coinId,
          networkKey: n.networkKey,
          chainName: n.chainName,
        });
      }
    }
  }
  return rows;
};

const CryptoWithdrawTab = ({ userId, initialConfig }) => {
  const [enabled, setEnabled] = useState(!!initialConfig?.enabled);
  const [addresses, setAddresses] = useState(flattenConfig(initialConfig));

  const [coins, setCoins] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [coinsLoading, setCoinsLoading] = useState(false);
  const [networksLoading, setNetworksLoading] = useState(false);

  const [form, setForm] = useState({ coinId: undefined, networkKey: undefined, address: "", memo: "" });
  // The change waiting on an emailed OTP. Nothing is sent to the API (and the
  // switches/rows don't change) until the admin confirms in AdminOtpModal.
  //   { action: ENABLE | DISABLE | ADD_ADDRESS | TOGGLE_ADDRESS | DELETE_ADDRESS,
  //     form?: <add form snapshot>, row?: <address row> }
  const [pending, setPending] = useState(null);

  // Full objects (not just the {value,label} picker options) so validation
  // can see chainType/addressRegex/memoRequired for the selected network.
  const selectedCoin = useMemo(() => coins.find((c) => c.coinId === form.coinId), [coins, form.coinId]);
  const selectedNetwork = useMemo(
    () => networks.find((n) => n.networkKey === form.networkKey),
    [networks, form.networkKey]
  );

  const addressError = useMemo(() => {
    if (!form.address) return null;
    return validateWithdrawAddress(form.address, selectedNetwork, selectedCoin?.symbol);
  }, [form.address, selectedNetwork, selectedCoin]);

  const memoError =
    selectedNetwork?.memoRequired && !form.memo.trim() ? "This network requires a memo / destination tag" : null;

  const canAdd =
    !!form.coinId &&
    !!form.networkKey &&
    !!form.address.trim() &&
    !addressError &&
    !memoError;

  const applyConfig = (cfg) => {
    if (!cfg) return;
    setEnabled(!!cfg.enabled);
    setAddresses(flattenConfig(cfg));
  };

  // Load the withdraw-enabled coin catalog once the manager is on.
  useEffect(() => {
    if (!enabled || coins.length) return;
    setCoinsLoading(true);
    getWithdrawEnabledCoins()
      .then((res) => setCoins(res?.data ?? []))
      .catch(() => toast.error("Failed to load coins"))
      .finally(() => setCoinsLoading(false));
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (checked) => setPending({ action: checked ? "ENABLE" : "DISABLE" });

  const handleCoinChange = (coinId) => {
    setForm({ coinId, networkKey: undefined, address: form.address, memo: "" });
    setNetworks([]);
    if (!coinId) return;
    setNetworksLoading(true);
    getWithdrawCoinNetworks(coinId)
      .then((res) => setNetworks(res?.data ?? []))
      .catch(() => toast.error("Failed to load networks"))
      .finally(() => setNetworksLoading(false));
  };

  const handleAdd = () => {
    if (!form.coinId || !form.networkKey || !form.address.trim()) {
      toast.warn("Select a coin, network and enter an address");
      return;
    }
    if (addressError) {
      toast.warn(addressError);
      return;
    }
    if (memoError) {
      toast.warn(memoError);
      return;
    }
    setPending({
      action: "ADD_ADDRESS",
      form: {
        coinId: form.coinId,
        networkKey: form.networkKey,
        address: form.address.trim(),
        memo: form.memo.trim(),
        symbol: selectedCoin?.symbol,
        chainName: selectedNetwork?.chainName,
      },
    });
  };

  const handleToggleAddress = (row) => setPending({ action: "TOGGLE_ADDRESS", row });
  const handleDeleteAddress = (row) => setPending({ action: "DELETE_ADDRESS", row });

  // What the OTP modal shows / sends for the pending change.
  const otpConfig = useMemo(() => {
    if (!pending) return null;
    const { action, form: f, row } = pending;
    if (action === "ENABLE" || action === "DISABLE") {
      return {
        title: action === "ENABLE" ? "Enable crypto withdrawal" : "Disable crypto withdrawal",
        rows: [["Action", action === "ENABLE" ? "Enable crypto withdrawal" : "Disable crypto withdrawal"]],
        warning: action === "ENABLE" ? "Enabling lets this user withdraw crypto to the addresses added for them." : undefined,
        danger: action === "DISABLE",
        confirmText: action === "ENABLE" ? "Enable" : "Disable",
        sendBody: { action },
      };
    }
    if (action === "ADD_ADDRESS") {
      return {
        title: "Add withdrawal address",
        rows: [
          ["Coin", f.symbol],
          ["Network", f.chainName],
          ["Address", f.address],
          ...(f.memo ? [["Memo / Tag", f.memo]] : []),
        ],
        warning: "This opens a crypto withdrawal path for this user to the address below.",
        danger: false,
        confirmText: "Add Address",
        sendBody: { action, coinId: f.coinId, networkKey: f.networkKey, address: f.address, ...(f.memo ? { memo: f.memo } : {}) },
      };
    }
    const rowSummary = [
      ["Coin", row.symbol],
      ["Network", row.chainName],
      ["Address", row.address],
      ...(row.memo ? [["Memo / Tag", row.memo]] : []),
    ];
    if (action === "DELETE_ADDRESS") {
      return {
        title: "Delete withdrawal address",
        rows: rowSummary,
        warning: "This permanently deletes the address.",
        danger: true,
        confirmText: "Delete",
        sendBody: { action, addressId: row._id },
      };
    }
    return {
      title: row.isActive ? "Deactivate withdrawal address" : "Activate withdrawal address",
      rows: rowSummary,
      warning: row.isActive ? undefined : "Activating re-opens a crypto withdrawal path for this user to the address below.",
      danger: row.isActive,
      confirmText: row.isActive ? "Deactivate" : "Activate",
      sendBody: { action, addressId: row._id },
    };
  }, [pending]);

  // Runs the pending change with the OTP. Rejects (so the modal can show the
  // error inline) on failure; closes the modal on success.
  const executePending = async (otp) => {
    const { action, form: f, row } = pending;
    let res;
    if (action === "ENABLE" || action === "DISABLE") {
      res = await toggleUserCryptoWithdraw(userId, action === "ENABLE", otp);
      applyConfig(res?.data);
      setEnabled(action === "ENABLE"); // fallback if response omits config
    } else if (action === "ADD_ADDRESS") {
      res = await addUserWithdrawAddress(
        userId,
        { coinId: f.coinId, networkKey: f.networkKey, address: f.address, memo: f.memo || undefined },
        otp
      );
      applyConfig(res?.data);
      setForm({ coinId: undefined, networkKey: undefined, address: "", memo: "" });
      setNetworks([]);
    } else if (action === "TOGGLE_ADDRESS") {
      res = await toggleUserWithdrawAddress(userId, row._id, otp);
      applyConfig(res?.data);
    } else {
      res = await deleteUserWithdrawAddress(userId, row._id, otp);
      applyConfig(res?.data);
    }
    toast.success(res?.message || "Updated");
    setPending(null);
  };

  const coinOptions = useMemo(
    () => coins.map((c) => ({ value: c.coinId, label: `${c.symbol} — ${c.name}` })),
    [coins]
  );
  const networkOptions = useMemo(
    () => networks.map((n) => ({ value: n.networkKey, label: n.chainName })),
    [networks]
  );

  const columns = [
    { title: "Coin", dataIndex: "symbol", key: "symbol", render: (v) => <b>{v}</b> },
    { title: "Network", dataIndex: "chainName", key: "chainName" },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (v) => <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{v}</span>,
    },
    {
      title: "Memo/Tag",
      dataIndex: "memo",
      key: "memo",
      render: (v) => (v ? <span style={{ fontFamily: "monospace" }}>{v}</span> : <span style={{ color: "#bbb" }}>—</span>),
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => (
        <Tag color={r.isActive ? "green" : "default"}>{r.isActive ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <PermissionGate module="user" action="update">
            <Switch
              size="small"
              checked={r.isActive}
              onChange={() => handleToggleAddress(r)}
              checkedChildren="On"
              unCheckedChildren="Off"
            />
            <Button danger size="small" icon={<FiTrash2 />} onClick={() => handleDeleteAddress(r)} />
          </PermissionGate>
        </div>
      ),
    },
  ];

  return (
    <DetailSection title="Crypto Withdraw Manager">
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <span style={{ fontWeight: 600 }}>Crypto Withdrawal</span>
        <PermissionGate
          module="user"
          action="update"
          fallback={<Tag color={enabled ? "green" : "default"}>{enabled ? "Enabled" : "Disabled"}</Tag>}
        >
          <Switch
            checked={enabled}
            onChange={handleToggle}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
        </PermissionGate>
      </div>

      {enabled && (
        <>
          <p style={{ color: "#666", fontSize: 13, marginBottom: 14 }}>
            The user may withdraw crypto only to the active addresses below. Multiple addresses for
            the same coin and network are allowed.
          </p>

          <PermissionGate module="user" action="update">
            <div
              style={{
                marginBottom: 20,
                padding: 16,
                background: "#fafafa",
                borderRadius: 8,
              }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start" }}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select coin"
                  style={{ minWidth: 200 }}
                  loading={coinsLoading}
                  value={form.coinId}
                  onChange={handleCoinChange}
                  options={coinOptions}
                />
                <Select
                  placeholder="Select network"
                  style={{ minWidth: 200 }}
                  loading={networksLoading}
                  value={form.networkKey}
                  disabled={!form.coinId}
                  onChange={(v) => setForm({ ...form, networkKey: v, memo: "" })}
                  options={networkOptions}
                />
                <div style={{ flex: 1, minWidth: 260 }}>
                  <Input
                    placeholder="Destination address"
                    status={addressError ? "error" : undefined}
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    onPressEnter={handleAdd}
                  />
                  {addressError && (
                    <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>{addressError}</div>
                  )}
                </div>
                {selectedNetwork?.memoRequired && (
                  <div style={{ minWidth: 200 }}>
                    <Input
                      placeholder="Memo / destination tag"
                      status={memoError ? "error" : undefined}
                      value={form.memo}
                      onChange={(e) => setForm({ ...form, memo: e.target.value })}
                      onPressEnter={handleAdd}
                    />
                    {memoError && <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>{memoError}</div>}
                  </div>
                )}
                <Button type="primary" disabled={!canAdd} onClick={handleAdd}>
                  Add Address
                </Button>
              </div>
            </div>
          </PermissionGate>

          <Table
            rowKey="_id"
            size="small"
            columns={columns}
            dataSource={addresses}
            pagination={false}
            locale={{ emptyText: "No withdrawal addresses added yet" }}
          />
        </>
      )}

      {otpConfig && (
        <AdminOtpModal
          open
          title={otpConfig.title}
          rows={otpConfig.rows}
          warning={otpConfig.warning}
          danger={otpConfig.danger}
          confirmText={otpConfig.confirmText}
          onSendOtp={() => sendCryptoWithdrawOtpApi(userId, otpConfig.sendBody)}
          onConfirm={executePending}
          onClose={() => setPending(null)}
        />
      )}
    </DetailSection>
  );
};

export default CryptoWithdrawTab;
